import type { Client } from './types.js';

const WEEKEND_ICON_SVG =
  '<svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">' +
  '<path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"/>' +
  '</svg>';

export type ClientListDom = {
  filterInput: HTMLInputElement;
  selectAllBtn: HTMLElement;
  selectWeekendBtn: HTMLElement;
  deselectAllBtn: HTMLElement;
  listEl: HTMLElement;
  countEl: HTMLElement;
};

export class ClientList {
  private clients: Client[] = [];
  private readonly checkedSlugs = new Set<string>();

  constructor(
    private readonly dom: ClientListDom,
    private readonly onSelectionChange: () => void,
  ) {
    this.dom.filterInput.addEventListener('input', () => this.render());
    this.dom.selectAllBtn.addEventListener('click', () => this.selectAll());
    this.dom.selectWeekendBtn.addEventListener('click', () => this.selectWeekend());
    this.dom.deselectAllBtn.addEventListener('click', () => this.deselectAll());
  }

  setClients(clients: Client[]): void {
    this.clients = clients;
  }

  setSelectedSlugs(slugs: Iterable<string>): void {
    this.checkedSlugs.clear();
    for (const s of slugs) this.checkedSlugs.add(s);
  }

  getSelectedSlugs(): string[] {
    return [...this.checkedSlugs];
  }

  hasSelection(): boolean {
    return this.checkedSlugs.size > 0;
  }

  render(): void {
    const filter = this.dom.filterInput.value.toLowerCase();
    this.dom.listEl.innerHTML = '';

    const visible = this.clients.filter(
      (c) =>
        c.name.toLowerCase().includes(filter) ||
        c.client_slug.toLowerCase().includes(filter),
    );

    if (visible.length === 0) {
      this.dom.listEl.innerHTML =
        '<div class="empty-msg">No clients match your filter.</div>';
      return;
    }

    for (const c of visible) {
      const label = document.createElement('label');
      label.className =
        'client-item' + (this.checkedSlugs.has(c.client_slug) ? ' checked' : '');

      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.value = c.client_slug;
      cb.checked = this.checkedSlugs.has(c.client_slug);
      cb.addEventListener('change', () => {
        if (cb.checked) {
          this.checkedSlugs.add(c.client_slug);
          label.classList.add('checked');
        } else {
          this.checkedSlugs.delete(c.client_slug);
          label.classList.remove('checked');
        }
        this.updateCount();
        this.onSelectionChange();
      });

      const nameSpan = document.createElement('span');
      nameSpan.className = 'name';
      nameSpan.textContent = c.name;

      if (c.is_weekend_sender) {
        const weekendIcon = document.createElement('span');
        weekendIcon.className = 'weekend-icon';
        weekendIcon.title = 'Weekend sender';
        weekendIcon.innerHTML = WEEKEND_ICON_SVG;
        label.append(cb, nameSpan, weekendIcon);
      } else {
        label.append(cb, nameSpan);
      }
      this.dom.listEl.appendChild(label);
    }
  }

  updateCount(): void {
    const n = this.checkedSlugs.size;
    this.dom.countEl.textContent =
      n === 0 ? '0 selected' : n === 1 ? '1 selected' : `${n} selected`;
    this.dom.countEl.classList.toggle('has-selection', n > 0);
  }

  private selectAll(): void {
    for (const c of this.clients) this.checkedSlugs.add(c.client_slug);
    this.render();
    this.updateCount();
    this.onSelectionChange();
  }

  private selectWeekend(): void {
    for (const c of this.clients) {
      if (c.is_weekend_sender) this.checkedSlugs.add(c.client_slug);
    }
    this.render();
    this.updateCount();
    this.onSelectionChange();
  }

  private deselectAll(): void {
    this.checkedSlugs.clear();
    this.render();
    this.updateCount();
    this.onSelectionChange();
  }
}
